import { useMemo } from 'react';
import {
  Button,
  DatePicker,
  Icon,
  SelectMenu,
  Switch,
  TextField,
  Typography,
} from '@scale/scaleui';
import { ReviewLevel } from '@scale/shared-types';
import MultiSelect from 'corp/components/UI/Select';
import {
  CompassCardComponent,
  CompassCardComponentProps,
} from 'react-scale/corp/views/DataCompassV2/types';
import useUpdateDirectAssignmentCard from './useUpdateDirectAssignmentCard';
import {
  REVIEW_LEVEL_OPTIONS,
  NullReviewLevel,
  MAX_DIRECT_ASSIGNMENTS_PER_USER,
} from '@scale/shared/defaults';
import type { DynamicAssignmentEntry } from '@scale/genai-compass-shared/cards/update_direct_assignment';
import { twMerge } from 'tailwind-merge';
import _ from 'lodash';
import { Column } from '@scale/compass-shared/types';
import {
  DEFAULT_EXPIRE_DATE,
  DEFAULT_EXPIRE_HOUR,
  ADD_REMOVE_OPTIONS,
  ASSIGNMENT_RANK_OPTIONS,
} from './constants';

/**
 * If the dynamic-da-card feature flag is enabled, renders a switch above the field to allow
 * selecting a column from previous results instead of adding an explicit value for the field.
 */
const DynamicAssignmentField = ({
  assignments,
  setAssignments,
  index,
  allowDynamicFieldValues,
  fieldName,
  value,
  availableColumns,
  disabled,
  handleChange,
  children,
}: {
  assignments: DynamicAssignmentEntry[];
  setAssignments: (assignments: DynamicAssignmentEntry[]) => void;
  index: number;
  allowDynamicFieldValues: boolean;
  fieldName: keyof NonNullable<DynamicAssignmentEntry['dynamicConfig']>;
  value: any;
  availableColumns: Column[];
  disabled?: boolean;
  handleChange: (field: keyof DynamicAssignmentEntry, value: any) => void;
  children: React.ReactNode;
}) => {
  /**
   * Clear the current field value and update dynamicConfig
   */
  const onUpdateDynamicSwitch = (
    field: keyof NonNullable<DynamicAssignmentEntry['dynamicConfig']>,
    value: boolean,
  ) => {
    const newAssignments = [...assignments];

    const newDynamicConfig = newAssignments[index].dynamicConfig ?? {
      projectId: false,
      reviewLevel: false,
    };

    newDynamicConfig[field] = value;

    newAssignments[index] = {
      ...newAssignments[index],
      dynamicConfig: newDynamicConfig,
      [field]: undefined,
    };
    setAssignments(newAssignments);
  };

  return (
    <>
      {allowDynamicFieldValues && (
        <Switch
          checked={assignments[index].dynamicConfig?.[fieldName] ?? false}
          onCheckedChange={() =>
            onUpdateDynamicSwitch(fieldName, !assignments[index].dynamicConfig?.[fieldName])
          }
          disabled={disabled}
          labelDescription={`Whether or not to pull the ${fieldName} from a column`}
          label="Dynamic"
        />
      )}
      {allowDynamicFieldValues && assignments[index].dynamicConfig?.[fieldName] ? (
        <MultiSelect
          multi={false}
          className="w-72"
          placeholder={`Select a column with ${fieldName}s`}
          options={availableColumns.map(column => {
            return {
              value: column.id ?? '-',
              label: column.header ?? '-',
            };
          })}
          value={value}
          onChange={(e: { value: string }) => handleChange(fieldName, e.value)}
          disabled={disabled}
        />
      ) : (
        children
      )}
    </>
  );
};

const AssignmentRow = ({
  removeAllAssignments,
  assignments,
  setAssignments,
  index,
  isDynamicCardVersion,
  availableColumns,
}: {
  removeAllAssignments: boolean;
  assignments: DynamicAssignmentEntry[];
  setAssignments: (assignments: DynamicAssignmentEntry[]) => void;
  index: number;
  isDynamicCardVersion: boolean;
  availableColumns: Column[];
}) => {
  const handleChange = (field: keyof DynamicAssignmentEntry, value: any) => {
    const newAssignments = [...assignments];
    newAssignments[index] = { ...newAssignments[index], [field]: value };
    setAssignments(newAssignments);
  };

  const addAssignment = () => {
    if (assignments.length < MAX_DIRECT_ASSIGNMENTS_PER_USER) {
      setAssignments([
        ...assignments,
        { projectId: '', reviewLevel: undefined, expiresAt: DEFAULT_EXPIRE_DATE },
      ]);
    }
  };

  const removeAssignment = () => {
    const newAssignments = assignments.filter((_, i) => i !== index); // create a new array without the assignment at index 'index'
    setAssignments(newAssignments);
  };

  const isLastRow = index === assignments.length - 1;
  const canAddMore = assignments.length < MAX_DIRECT_ASSIGNMENTS_PER_USER;
  const canRemove = assignments.length > 1;
  const isRemoveRow = assignments[index].shouldRemove;

  return (
    <div className={twMerge('flex flex-col gap-4 rounded-lg w-full')}>
      <div className="flex gap-4">
        <div className={`flex flex-col ${isDynamicCardVersion && 'gap-2'}`}>
          <Typography.Paragraph2>Project ID*</Typography.Paragraph2>

          <DynamicAssignmentField
            fieldName="projectId"
            allowDynamicFieldValues={isDynamicCardVersion}
            assignments={assignments}
            index={index}
            setAssignments={setAssignments}
            value={assignments[index].projectId}
            disabled={removeAllAssignments}
            availableColumns={availableColumns}
            handleChange={handleChange}
          >
            <TextField
              className="w-72"
              placeholder="Input the project ID..."
              value={assignments[index].projectId}
              onChange={e => handleChange('projectId', e.target.value)}
              disabled={removeAllAssignments}
            />
          </DynamicAssignmentField>
        </div>
        <div className={`flex flex-col ${isDynamicCardVersion && 'gap-2'}`}>
          <Typography.Paragraph2>Review Level</Typography.Paragraph2>

          <DynamicAssignmentField
            fieldName="reviewLevel"
            allowDynamicFieldValues={isDynamicCardVersion}
            assignments={assignments}
            index={index}
            setAssignments={setAssignments}
            value={assignments[index].reviewLevel}
            availableColumns={availableColumns}
            disabled={removeAllAssignments}
            handleChange={handleChange}
          >
            <SelectMenu
              className="w-72"
              placeholder="Select a review level..."
              options={REVIEW_LEVEL_OPTIONS}
              selectedOptionId={assignments[index].reviewLevel ?? NullReviewLevel.NO_REVIEW_LEVEL}
              setSelected={level =>
                handleChange(
                  'reviewLevel',
                  level === NullReviewLevel.NO_REVIEW_LEVEL || _.isNil(level)
                    ? undefined
                    : (level as ReviewLevel),
                )
              }
              disabled={removeAllAssignments}
            />
          </DynamicAssignmentField>
        </div>
        <div className={`flex flex-col ${isDynamicCardVersion && 'justify-between'}`}>
          <Typography.Paragraph2>Expiration Date</Typography.Paragraph2>
          <DatePicker
            canToggleDate
            value={
              typeof assignments[index].expiresAt === 'string'
                ? new Date(assignments[index].expiresAt!)
                : assignments[index].expiresAt
            }
            onChange={(value: Date | null) => {
              if (value) {
                const date = new Date(value);
                date.setHours(DEFAULT_EXPIRE_HOUR);
                handleChange('expiresAt', date);
              } else {
                handleChange('expiresAt', undefined);
              }
            }}
            placeholder="Doesn't expire..."
            disabled={removeAllAssignments || isRemoveRow}
          />
        </div>
        {isDynamicCardVersion && (
          <>
            <div className={'flex flex-col justify-between'}>
              <Typography.Paragraph2>Add / Remove</Typography.Paragraph2>
              <SelectMenu
                className="w-48"
                placeholder="Choose add or remove"
                options={ADD_REMOVE_OPTIONS}
                selectedOptionId={assignments[index].shouldRemove ? 'remove' : 'add'}
                setSelected={value => handleChange('shouldRemove', value === 'remove')}
                disabled={removeAllAssignments}
              />
            </div>
          </>
        )}
        {canRemove && (
          <div className="flex items-end pb-2">
            <Icon
              icon={['fas', 'minus']}
              onClick={removeAssignment}
              rounded
              size="sm"
              tooltip="Remove assignment"
              variant="neutral"
            />
          </div>
        )}
      </div>
      {isLastRow && canAddMore && (
        <div className="flex flex-row gap-2">
          <Button
            icon={{
              position: 'left',
              type: ['fas', 'plus'],
            }}
            size="lg"
            onClick={addAssignment}
            variant="transparent"
            disabled={removeAllAssignments}
          >
            Add Assignment
          </Button>
        </div>
      )}
    </div>
  );
};

const UpdateDirectAssignmentCardComponent: CompassCardComponent = ({
  index,
}: CompassCardComponentProps) => {
  const {
    columnIdWithWorkerIds,
    setColumnIdWithWorkerIds,
    assignments,
    setAssignments,
    capacityRequestId,
    setCapacityRequestId,
    overwrite,
    setOverwrite,
    removeAllAssignments,
    setRemoveAllAssignments,
    // numWorkers,
    // card,
    availableColumns,
    isDynamicCardVersion,
    assignmentRank,
    setAssignmentRank,
  } = useUpdateDirectAssignmentCard(index);

  const isAllRemovals = useMemo(() => {
    return assignments.every(assignment => assignment.shouldRemove);
  }, [assignments]);

  return (
    <div className="flex flex-col gap-4">
      <Typography.Subtitle2>
        Update the direct assignments for all selected workers.
      </Typography.Subtitle2>
      <Typography.Paragraph2>
        This card cannot be used again in the same workflow. Please start a new workflow to run
        additional direct assigns.
      </Typography.Paragraph2>
      <div className="flex flex-col gap-2 w-full">
        <div className="flex flex-col gap-2">
          <Typography.Paragraph2>Column with Worker IDs</Typography.Paragraph2>
          <div className="flex items-center gap-2">
            <MultiSelect
              className="w-60 rounded-lg"
              multi={false}
              options={availableColumns.map(column => {
                return {
                  value: column.id ?? '-',
                  label: column.header ?? '-',
                };
              })}
              disabled={false}
              placeholder="Select a column with worker ids"
              isSearchable={true}
              isClearable={true}
              value={columnIdWithWorkerIds}
              onChange={(e: any) => {
                setColumnIdWithWorkerIds(e === null ? null : e.value);
              }}
            />
            {isDynamicCardVersion && (
              <Icon
                icon={['fas', 'info-circle']}
                rounded
                size="md"
                tooltip="Note: if the same worker ID appears multiple times, values from the first row they appear in will be used."
                variant="neutral"
              />
            )}
          </div>
        </div>
        <div className={'flex flex-col gap-4 my-2'}>
          {assignments.map((_, idx) => (
            <AssignmentRow
              key={idx}
              removeAllAssignments={removeAllAssignments ?? false}
              assignments={assignments}
              setAssignments={setAssignments}
              index={idx}
              isDynamicCardVersion={isDynamicCardVersion}
              availableColumns={availableColumns}
            />
          ))}
        </div>

        <div className="flex flex-col gap-4">
          {isDynamicCardVersion && (
            <SelectMenu
              className="w-48"
              placeholder="Select assignment rank..."
              options={ASSIGNMENT_RANK_OPTIONS}
              selectedOptionId={
                assignmentRank === undefined
                  ? 'push'
                  : ASSIGNMENT_RANK_OPTIONS[assignmentRank + 1]?.id || 'push'
              }
              setSelected={rank => setAssignmentRank(rank)}
              disabled={removeAllAssignments || isAllRemovals || overwrite}
              label="Assignment Rank"
              labelDescription="For added assignments, controls where they will be inserted into the users' assignment list. 'Push to end' adds to the end of the list, while specific positions insert at that position and push existing assignments down."
            />
          )}

          <div className="flex items-center">
            <Switch
              label="Overwrite"
              checked={removeAllAssignments ? false : overwrite}
              onCheckedChange={() => setOverwrite(!overwrite)}
              disabled={removeAllAssignments || isAllRemovals}
            />
            <Icon
              icon={['fas', 'info-circle']}
              rounded
              size="sm"
              tooltip="Use overwrite to replace the user's direct assignments array entirely. If unchecked, this tool will add projects to the users' assignment list and bump existing projects down (or off) the assignment list."
              variant="neutral"
            />
          </div>
          <div className="flex items-center">
            <Switch
              label="Remove all direct assignments"
              checked={removeAllAssignments}
              onCheckedChange={() => setRemoveAllAssignments(!removeAllAssignments)}
            />
            <Icon
              icon={['fas', 'info-circle']}
              rounded
              size="sm"
              tooltip="This will remove all existing direct assignments for the selected workers"
              variant="neutral"
            />
          </div>
          <div className="flex flex-col">
            <div className="flex items-center">
              <Typography.Paragraph2>Capacity Request ID*</Typography.Paragraph2>
              <Icon
                icon={['fas', 'info-circle']}
                rounded
                size="sm"
                tooltip={
                  <div>
                    Input the id if this direct assignment was requested via{' '}
                    <a
                      href="https://scale.atlassian.net/servicedesk/customer/portal/37/group/78/create/10426"
                      className="text-blue-500 underline"
                    >
                      Jira's Capacity Request form.
                    </a>
                    <br />
                    <br />
                    If there is no ticket ID, please put a detailed description of this request.
                    <br />
                    <br />
                    If there is a scheduled Compass workflow associated with this, please input a
                    link to the Compass flow.
                    <br />
                    <br />
                    If this is associated with a Slack message, please link the Slack message.
                  </div>
                }
                variant="neutral"
              />
            </div>
            <TextField
              className="w-80"
              placeholder="Input the Capacity Request ID..."
              value={capacityRequestId}
              onChange={e => setCapacityRequestId(e.target.value)}
            />
          </div>
          {isDynamicCardVersion && overwrite && (
            <div className="flex gap-2 items-center mt-2">
              <Icon icon={['fas', 'exclamation-triangle']} rounded size="sm" variant="neutral" />
              <Typography.Paragraph2>
                Warning! This will replace the users' direct assignments array entirely. Please
                double-check that the overwrite toggle is checked intentionally.
              </Typography.Paragraph2>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default UpdateDirectAssignmentCardComponent;
